import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { EdiDespatchAdvice, EdiDispatchParcel, GungEdiItemIds } from '../../../models/edi';
import { ProductService } from '../../../services/products/product.service';
import { first } from 'rxjs';
import { Product } from '../../../models/product';
import { CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';
import { TranslateService } from '@ngx-translate/core';

export interface EdiDispatchParcelMapped {
  batchNumber: string;
  ginIdentifier: string;
  id: string;
  parentGinIdentifier: string;
  items: EdiDispatchProductMapped[]
}

export interface EdiDispatchProductMapped {
  tempId: string;
  ids: GungEdiItemIds;
  name: string;
  quantity: number;
  volume?: number;
  weight?: number;
  selected?: boolean;
}

@Component({
  selector: 'lib-edi-dispatch-parcel-management',
  templateUrl: './edi-dispatch-parcel-management.component.html',
  styleUrl: './edi-dispatch-parcel-management.component.scss',
  standalone: false
})
export class EdiDispatchParcelManagementComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() data: EdiDespatchAdvice;
  @Output() saveParcelData = new EventEmitter<EdiDespatchAdvice>();

  protected productMappedData: EdiDispatchProductMapped[] = [];
  protected parcelMappedData: EdiDispatchParcelMapped[] = [];
  protected productIdMap: { [s: string]: Product } = {};

  @ViewChildren('parcelDropListRefs') parcelDropListRefs!: QueryList<CdkDropList>;
  connectedDropListsIds: string[] = [];

  constructor(
    protected productService: ProductService,
    protected gungModalService: GungModalService,
    protected translateService: TranslateService
  ) {}

  ngOnInit(): void {
    const ids: string[] = Array.from(new Set(this.data.items.map(i => i.ids.gungId)));
    this.productService.getFullProductsByIds(ids).pipe(first()).subscribe(products => {
      for (const p of products) {
        this.productIdMap[p.id] = p;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.buildParcelMappedData();
      this.buildProductMappedData();
    }
  }

  ngAfterViewInit(): void {
    this.updateConnectedDropLists();

    this.parcelDropListRefs.changes.subscribe(() => {
      this.updateConnectedDropLists();
    });
  }

  private updateConnectedDropLists(): void {
    const parcelIds = this.parcelDropListRefs.map((ref, i) => `parcelList${i}`);
    this.connectedDropListsIds = ['productList', ...parcelIds];
  }

  buildParcelMappedData(): void {
    this.parcelMappedData = [];
    let rn: number = 0;
    for (const item of this.data?.items) {
      let prn: number = 0;
      for (const parcel of item.parcels) {
        const foundIndex: number = this.parcelMappedData.findIndex(p => p.ginIdentifier === parcel.ginIdentifier);
        if (foundIndex > -1) {
          this.parcelMappedData[foundIndex].items.push({
            ids: item.ids,
            name: item.name,
            tempId: item.ids?.gungId + '_PR_' + rn + '_PR_' + prn,
            quantity: parcel.quantity,
            volume: parcel.volume,
            weight: parcel.weight
          });
        } else {
          this.parcelMappedData.push({
            ginIdentifier: parcel.ginIdentifier,
            id: parcel.id,
            parentGinIdentifier: parcel.parentGinIdentifier,
            batchNumber: parcel.batchNumber,
            items: [{
              ids: item.ids,
              name: item.name,
              tempId: item.ids?.gungId + '_PR_' + rn + '_PR_' + prn,
              quantity: parcel.quantity,
              volume: parcel.volume,
              weight: parcel.weight
            }]
          });
        }
      }
      rn++;
    }
  }

  buildProductMappedData(): void {
    this.productMappedData = [];
    let rn: number = 0;
    for (const item of this.data?.items) {
      const qtyInParcels: number = item.parcels?.reduce((sum, parcel) => sum + parcel.quantity, 0);
      if (qtyInParcels < item.quantities?.actualQuantity) {
        this.productMappedData.push({
          tempId: item.ids?.gungId + '_PR_' + rn,
          ids: item.ids,
          name: item.name,
          quantity: item.quantities.actualQuantity - qtyInParcels
        });
      }
      rn++;
    }
  }

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }

  selectAll(): void {
    this.productMappedData = this.productMappedData.map(p => {
      return {
        ...p,
        selected: true
      };
    });
  }

  openSplitProductModal(parcelProduct: EdiDispatchProductMapped): void {
    this.gungModalService.openEdiDispatchParcelProductSplitModal(parcelProduct, this.productIdMap[parcelProduct?.ids?.gungId]).then(
      (res: EdiDispatchProductMapped[]) => {
        if (res?.length > 0) {
          const foundIndex: number = this.productMappedData.findIndex(p => p.tempId === parcelProduct.tempId);
          if (foundIndex > -1) {
            this.productMappedData.splice(foundIndex, 1, ...res);
          }
        }
      },
      rej => {}
    )
  }

  dropSelectedProducts(index: number): void {
    const selectedProducts: EdiDispatchProductMapped[] = [];

    this.productMappedData = this.productMappedData.filter(p => {
      if (!p.selected) {
        return true;
      } else {
        selectedProducts.push(p);
        return false;
      }
    });

    for (const parcel of this.parcelMappedData) {
      parcel.items = parcel.items.filter(p => {
        if (!p.selected) {
          return true;
        } else {
          selectedProducts.push(p);
          return false;
        }
      });
    }

    this.parcelMappedData[index].items = [
      ...this.parcelMappedData[index].items,
      ...selectedProducts.map(p => { return { ...p, selected: false } })
    ];
  }

  openAddEditParcelModal(index?: number, parcel?: EdiDispatchParcelMapped): void {
    this.gungModalService.openAddEditParcelModal(structuredClone(parcel)).then(
      res => {
        if (!!res) {
          if (index !== undefined && index !== null) {
            this.parcelMappedData[index] = structuredClone(res);
          } else {
            this.parcelMappedData.push(structuredClone(res));
          }
        }
      },
      rej => {}
    );
  }

  removeParcel(index: number): void {
    this.gungModalService.openConfirmYesNoModal(
      this.translateService.instant('REMOVE'),
      this.translateService.instant('PARCEL_REMOVAL_MODAL_TEXT'),
      {},
      this.translateService.instant('YES'),
      this.translateService.instant('NO')
    ).then(
      res => {
        if (res && this.parcelMappedData?.[index]) {
          this.productMappedData = [...this.productMappedData, ...this.parcelMappedData[index].items];
          this.parcelMappedData.splice(index, 1);
        }
      },
      rej => {}
    );
  }

  openEditParcelProductDetails(index: number, product: EdiDispatchProductMapped): void {
    this.gungModalService.openEditParcelProductDetails(structuredClone(product)).then(
      res => {
        if (!!res) {
          const foundIndex: number = this.parcelMappedData[index].items.findIndex(p => p.tempId === product.tempId);
          if (foundIndex > -1) {
            this.parcelMappedData[index].items[foundIndex] = structuredClone(res);
          }
        }
      },
      rej => {}
    );
  }

  saveParcels(): void {
    const newData = structuredClone(this.data);
    const convertedData = this.getParcelsForItem();

    console.log(convertedData);

    let rn: number = 0;
    for (const item of newData.items) {
      item.parcels = convertedData[item.ids?.gungId + '_' + rn] || [];
      rn++;
    }

    this.saveParcelData.emit(newData);
  }

  getParcelsForItem(): { [s: string]: EdiDispatchParcel[] } {
    const res: { [s: string]: EdiDispatchParcel[] } = {};

    console.log(this.parcelMappedData);
    for (const parcel of this.parcelMappedData) {
      for (const item of parcel.items) {
        const parcelItemRowNumber: number = Number(item.tempId.split('_PR_')[1]);
        const foundDataIndex: number = this.data.items.findIndex(i => i.ids?.gungId === item.ids?.gungId);
        if (foundDataIndex > -1) {
          const parcelItem: EdiDispatchParcel = {
            id: parcel.id,
            parentGinIdentifier: parcel.parentGinIdentifier,
            batchNumber: parcel.batchNumber,
            ginIdentifier: parcel.ginIdentifier,
            volume: item.volume,
            weight: item.weight,
            quantity: item.quantity
          };

          if (!res[item.ids.gungId + '_' + parcelItemRowNumber]) {
            res[item.ids.gungId + '_' + parcelItemRowNumber] = [];
          }

          res[item.ids.gungId + '_' + parcelItemRowNumber].push(parcelItem);
        }
      }
    }

    return res;
  }
}
